import { useState } from "react";
import { lireStatutPaiement } from "../lib/api";
import type { PlanAchat } from "../lib/achat";
import { track } from "../lib/track";
import { useApp } from "../state/AppState";

/**
 * Retrouver un rapport payé depuis n'importe quel appareil — 25/08/2026.
 *
 * ## Le trou que ceci bouche
 *
 * Le produit ne demande ni compte, ni nom, ni adresse. La seule chose qui
 * reliait un acheteur à ce qu'il avait payé était donc **le stockage de son
 * navigateur** : `lireAchat()` dans `lib/achat.ts`, et rien d'autre. Tous les
 * chemins de récupération en dépendaient — le bandeau d'accueil, l'écran de
 * reprise, la fenêtre de paiement — et tous disaient la même phrase sans en
 * mesurer la portée : « un paiement est enregistré **sur cet appareil** ».
 *
 * Ce qui suffit tant que rien ne bouge, et ne suffit plus dès que quelque
 * chose bouge : un téléphone changé, un navigateur nettoyé, une navigation
 * privée, un achat fait sur le téléphone et relu sur l'ordinateur, ou
 * simplement les trente jours au bout desquels la trace locale s'efface
 * d'elle-même. Dans tous ces cas, quelqu'un avait payé trois mille francs et
 * le site lui reproposait de payer.
 *
 * ## Pourquoi la référence suffit, et pourquoi elle ne divulgue rien
 *
 * `txId` est un `randomUUID()` tiré côté serveur. Le connaître n'est pas une
 * indication, c'est une preuve : il ne se devine pas, il ne s'énumère pas, et
 * il n'est communiqué qu'à celui qui a ouvert le paiement. C'est exactement le
 * rôle que jouerait un mot de passe, sans avoir à en créer un — ce qui aurait
 * demandé un compte, donc une donnée nominative, donc l'inverse de la règle
 * n° 5.
 *
 * L'appel ne fait qu'interroger `/pay/status`, qui existait déjà et rendait
 * déjà `urlRapport` pour une transaction livrée. **Rien de nouveau n'est
 * ouvert côté serveur** : ce qui manquait était un endroit où coller la
 * référence.
 *
 * ## Ce qu'il fait de plus, une fois trouvé
 *
 * Il réarme la mémoire locale. L'appareil sur lequel on vient de retrouver son
 * rapport est celui sur lequel on le relira : le faire recommencer la
 * prochaine fois serait tenir la promesse une seule fois.
 */

/** Ce que l'écran a à dire, après une recherche. */
type Issue =
  | { etat: "repos" }
  | { etat: "cherche" }
  | { etat: "trouve"; url: string }
  | { etat: "en_cours"; message: string }
  /**
   * Livré, mais l'adresse a été fermée depuis l'exploitation — 02/09/2026.
   *
   * Ce cas tombait dans « en cours » et affichait « Votre rapport est prêt. »
   * sans aucun bouton. Une réussite annoncée qui ne mène nulle part est le pire
   * des états : la personne a payé, elle a raison de croire ce qu'elle lit, et
   * elle n'a rien à faire de ce qu'on lui dit.
   */
  | { etat: "adresse_fermee"; message: string }
  /**
   * Remboursé — 02/09/2026.
   *
   * Ce cas tombait dans « en cours », c'est-à-dire dans la branche qui dit
   * « patientez ». Rien n'arrivera : le remboursement a retiré l'accès au
   * document, et c'est la contrepartie assumée. Le ranger avec les rédactions
   * en train d'aboutir ferait attendre quelqu'un pour rien.
   */
  | { etat: "rembourse"; message: string; le: string | null }
  | { etat: "inconnu" };

/**
 * Une référence est un UUID. On accepte ce que les gens collent réellement —
 * espaces, retours à la ligne, majuscules, et le mot « référence » recopié
 * avec — puis on ne garde que ce qui a la forme voulue.
 */
export function nettoyerReference(brut: string): string | null {
  const candidat = brut.trim().toLowerCase().replace(/\s+/g, "");
  const trouve = candidat.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/,
  );
  return trouve ? trouve[0] : null;
}

/*
  `ouvertParDefaut` — 26/08/2026.

  Le repli par défaut est juste partout où ce composant vit : sur l'accueil ou
  un écran de résultat, c'est une réparation, pas une offre, et l'afficher
  déployé donnerait à croire que le produit s'attend à rater ses livraisons.

  Sur la page d'aide, la question est déjà posée par le titre au-dessus —
  « Vous avez payé et le rapport n'est pas arrivé ». Demander à quelqu'un de
  cliquer sur « J'ai payé un rapport et je ne le retrouve pas » juste en
  dessous, c'est lui faire redire ce qu'il vient de lire, sur l'écran où il est
  le plus pressé.
*/
export function RecupererRapport({
  plan = "report",
  ouvertParDefaut = false,
}: {
  plan?: PlanAchat;
  ouvertParDefaut?: boolean;
}) {
  const { noterAchat, say } = useApp();
  const [ouvert, setOuvert] = useState(ouvertParDefaut);
  const [saisie, setSaisie] = useState("");
  const [issue, setIssue] = useState<Issue>({ etat: "repos" });

  const chercher = async () => {
    const reference = nettoyerReference(saisie);
    if (reference === null) {
      setIssue({ etat: "inconnu" });
      say("Cette référence n'a pas la forme attendue");
      return;
    }

    setIssue({ etat: "cherche" });
    track("recuperation_tentee", {});
    const etat = await lireStatutPaiement(reference);

    if (etat === null) {
      setIssue({ etat: "inconnu" });
      say("Aucun paiement ne correspond à cette référence");
      return;
    }

    if (etat.urlRapport) {
      /*
        Le plan mémorisé est celui de l'écran d'où l'on vient, faute de mieux :
        `/pay/status` ne rend pas le plan, et l'inventer serait pire. Cela n'a
        aucune conséquence sur ce qui est rendu — le plan ne sert qu'à rouvrir
        la bonne fenêtre, et le rapport retrouvé est déjà là, tel qu'il a été
        rédigé.
      */
      noterAchat(reference, plan);
      setIssue({ etat: "trouve", url: etat.urlRapport });
      say("Rapport retrouvé");
      track("recuperation_reussie", {});
      return;
    }

    if (etat.statut === "rembourse") {
      /*
        Pas de `noterAchat` ici : la mémoire locale sert à rouvrir un rapport
        depuis cet appareil, et il n'y a plus de rapport à rouvrir. L'y inscrire
        ramènerait la personne vers une porte close à chaque visite.
      */
      setIssue({ etat: "rembourse", message: etat.message, le: etat.rembourseLe ?? null });
      say(etat.message);
      return;
    }

    noterAchat(reference, plan);
    if (etat.adresseFermee) {
      setIssue({ etat: "adresse_fermee", message: etat.message });
      say(etat.message);
      return;
    }
    setIssue({ etat: "en_cours", message: etat.message });
    say(etat.message);
  };

  if (!ouvert) {
    return (
      <p className="fine" style={{ marginTop: "16px" }}>
        <button type="button" className="lien-cible" onClick={() => setOuvert(true)}>
          J'ai payé un rapport et je ne le retrouve pas
        </button>
      </p>
    );
  }

  return (
    <div className="recuperer">
      <h3>Retrouver un rapport payé</h3>
      <p className="sub">
        Collez la référence qui vous a été donnée au moment du paiement. Elle ouvre votre rapport
        depuis n'importe quel appareil — c'est la seule chose que nous ayons de vous, puisque nous
        ne demandons ni compte, ni nom.
      </p>
      <label htmlFor="reference-achat">Votre référence de paiement</label>
      <input
        type="text"
        id="reference-achat"
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        placeholder="0000aaaa-0000-0000-0000-000000000000"
        aria-invalid={issue.etat === "inconnu"}
        value={saisie}
        onChange={(e) => {
          setSaisie(e.target.value);
          if (issue.etat === "inconnu") setIssue({ etat: "repos" });
        }}
        style={{ marginBottom: "12px" }}
      />
      <button
        type="button"
        className="btn btn-ghost"
        disabled={issue.etat === "cherche"}
        onClick={() => void chercher()}
      >
        {issue.etat === "cherche" ? "Recherche…" : "Retrouver mon rapport"}
      </button>

      <div aria-live="polite">
        {issue.etat === "trouve" && (
          <p style={{ marginTop: "14px" }}>
            <a className="btn btn-brass" href={issue.url}>
              Télécharger mon rapport
            </a>
          </p>
        )}
        {issue.etat === "en_cours" && (
          <p className="fine" style={{ marginTop: "12px" }}>
            {issue.message}
          </p>
        )}
        {issue.etat === "rembourse" && (
          <>
            <p className="fine" style={{ marginTop: "12px" }}>
              {issue.message}
            </p>
            {issue.le !== null && (
              <p className="fine">Remboursement consigné le {issue.le.slice(0, 10)}.</p>
            )}
            <p style={{ marginTop: "8px" }}>
              <a className="btn btn-ghost" href="/aide">
                Nous écrire
              </a>
            </p>
          </>
        )}
        {issue.etat === "adresse_fermee" && (
          <>
            <p className="fine" style={{ marginTop: "12px" }}>
              {issue.message}
            </p>
            {/*
              Une phrase seule laisserait la personne chercher où écrire. Le
              lien porte le geste, comme partout ailleurs dans ce produit.
            */}
            <p style={{ marginTop: "8px" }}>
              <a className="btn btn-ghost" href="/aide">
                Nous écrire
              </a>
            </p>
          </>
        )}
        {/*
          Dire « inconnue » plutôt que rester vague.

          On pourrait craindre d'en dire trop — un écran qui distingue « existe »
          de « n'existe pas » est parfois un oracle. Il ne l'est pas ici : une
          référence est un identifiant aléatoire de cent vingt-huit bits, que
          personne ne trouve en essayant. Le seul effet de rester vague serait de
          laisser quelqu'un qui s'est trompé d'un caractère croire que son
          rapport a disparu.
        */}
        {issue.etat === "inconnu" && (
          <p className="fine" style={{ marginTop: "12px" }}>
            Aucun paiement ne correspond à cette référence. Vérifiez que vous l'avez copiée
            entièrement — elle fait trente-six caractères, tirets compris.
          </p>
        )}
      </div>
    </div>
  );
}
